import { DestroyRef, Directive, effect, ElementRef, HostListener, inject, signal, untracked } from '@angular/core'

import { getAudioDurationFromBlob } from '../utils/get-audio-duration-from-blob'

@Directive({
  selector: 'audio[sbBlobPlayer], video[sbBlobPlayer]',
  exportAs: 'sbBlobPlayer',
})
export class BlobPlayerDirective {
  private static readonly activePlayer = signal<BlobPlayerDirective | null>(null)

  private readonly elementRef = inject<ElementRef<HTMLAudioElement | HTMLVideoElement>>(ElementRef)
  private readonly destroyRef = inject(DestroyRef)
  private readonly player = this.elementRef.nativeElement

  readonly playing = signal(false)
  readonly currentTime = signal(0)
  readonly duration = signal(0)

  private animationFrameId: number | null = null
  private cachedDuration: { src: string; durationMs: number } | null = null

  constructor() {
    effect(() => {
      const activePlayer = BlobPlayerDirective.activePlayer()

      untracked(() => {
        if (activePlayer !== null && activePlayer !== this) {
          this.stopAndReset()
        }
      })
    })

    this.destroyRef.onDestroy(() => {
      if (BlobPlayerDirective.activePlayer() === this) {
        BlobPlayerDirective.activePlayer.set(null)
      }
    })
  }

  private get mediaDurationMs(): number {
    return Math.floor(this.player.duration) * 1000
  }

  private get mediaCurrentTimeMs(): number {
    return Math.floor(this.player.currentTime * 1000)
  }

  @HostListener('loadedmetadata')
  async onLoadedMetadata(): Promise<void> {
    const currentSrc = this.player.src

    if (this.cachedDuration?.src === currentSrc) {
      this.duration.set(this.cachedDuration.durationMs)
      return
    }

    if (Number.isFinite(this.player.duration)) {
      const durationMs = this.mediaDurationMs
      this.cachedDuration = { src: currentSrc, durationMs }
      this.duration.set(durationMs)
      return
    }

    if (!(this.player instanceof HTMLAudioElement)) {
      return
    }

    // Chrome & Firefox bug: MediaRecorder blobs lack duration metadata
    // Web Audio API decodes the entire file and reports the correct duration
    const response = await fetch(currentSrc)
    const blob = await response.blob()
    const durationMs = (await getAudioDurationFromBlob(blob)) * 1000
    this.cachedDuration = { src: currentSrc, durationMs }
    this.duration.set(durationMs)
  }

  @HostListener('play')
  onPlay(): void {
    BlobPlayerDirective.activePlayer.set(this)
    this.playing.set(true)
    this.startProgressLoop()
  }

  @HostListener('pause')
  onPause(): void {
    this.playing.set(false)
    this.stopProgressLoop()
  }

  @HostListener('ended')
  onEnded(): void {
    this.playing.set(false)
    this.stopProgressLoop()
    this.currentTime.set(0)
  }

  private shouldPlayOnRevisit = false

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      if (this.playing()) {
        this.pause()
        this.shouldPlayOnRevisit = true
      }
    } else {
      if (this.shouldPlayOnRevisit) {
        this.shouldPlayOnRevisit = false
        this.play()
      }
    }
  }

  play(): void {
    if (this.player.paused) {
      void this.player.play()
    }
  }

  pause(): void {
    this.player.pause()
  }

  toggle(): void {
    if (this.player.paused) {
      void this.player.play()
    } else {
      this.player.pause()
    }
  }

  private startProgressLoop(): void {
    const update = () => {
      if (this.playing()) {
        this.currentTime.set(this.mediaCurrentTimeMs)
        this.animationFrameId = requestAnimationFrame(update)
      }
    }
    this.animationFrameId = requestAnimationFrame(update)
  }

  private stopProgressLoop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }
  }

  private stopAndReset(): void {
    if (!this.player.paused) {
      this.player.pause()
      this.player.currentTime = 0
      this.currentTime.set(0)
    }
  }
}
